import React from 'react';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import SettingsIcon from '@mui/icons-material/Settings';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { CommandBar } from './CommandBar';
import type { CommandItem } from './CommandBar';
import type { ListChromeId, PinSlot } from '@/shared/form/commandPin';
import type { Density } from '@/shared/density/densityTokens';
import type { ListViewKind } from '@/shared/listViews/viewCandidates';

interface ListToolbarProps {
  title?: string;
  primaryAction?: { label: string; icon?: React.ReactNode; onClick: (e: React.MouseEvent<HTMLElement>) => void };
  secondary?: CommandItem[];
  overflow?: CommandItem[];
  onPin?: (id: string, slot: PinSlot) => void;
  onResetPins?: () => void;
  search?: { value: string; onChange: (v: string) => void; placeholder?: string };
  /** Forwarded to the search TextField's inputRef — lets a parent focus it programmatically (e.g. the `/` keyboard shortcut). */
  searchInputRef?: React.Ref<HTMLInputElement>;
  density?: { value: Density; onChange: (d: Density) => void };
  view?: {
    value: ListViewKind;
    onChange: (v: ListViewKind) => void;
    available: ListViewKind[];
  };
  /** Always-on extras (kanban column picker). Never in More. */
  filters?: React.ReactNode;
  /** Filters chip. Renders on the bar when Filters is pinned. */
  filterPicker?: React.ReactNode;
  /** Group by chip only. Renders on the bar when GroupBy is pinned. */
  fieldPickers?: React.ReactNode;
  actions?: React.ReactNode;
  /** Icon-only chrome for the bar; More keeps `actions` (labeled). */
  barActions?: React.ReactNode;
  chromePins?: Partial<Record<ListChromeId, PinSlot>>;
  /** Gear. Render only when set. */
  onOpenSettings?: () => void;
  busy?: boolean;
  /** When true, omit toolbar's own borderBottom (ListSurface already draws the divider). Default false. */
  embedded?: boolean;
}

export const ListToolbar: React.FC<ListToolbarProps> = ({
  title, primaryAction, secondary, overflow, onPin, onResetPins, search, searchInputRef, density, view, filters, filterPicker, fieldPickers, actions, barActions, chromePins, onOpenSettings, busy, embedded = false,
}) => {
  const slotOf = (id: ListChromeId): PinSlot => chromePins?.[id] ?? 'overflow'

  const viewToggle = (labeled: boolean) => {
    if (!view || view.available.length <= 1) return null
    const chip = (value: ListViewKind, label: string, aria: string, icon: React.ReactNode) => {
      const btn = (
        <ToggleButton value={value} aria-label={aria} sx={{ px: labeled ? 1.25 : 1, gap: labeled ? 0.75 : 0, textTransform: 'none' }}>
          {icon}
          {labeled ? label : null}
        </ToggleButton>
      )
      return labeled ? <React.Fragment key={value}>{btn}</React.Fragment> : <Tooltip key={value} title={aria}>{btn}</Tooltip>
    }
    return (
      <ToggleButtonGroup
        size="small"
        exclusive
        value={view.value}
        onChange={(_, v) => { if (v) view.onChange(v as ListViewKind); }}
      >
        {view.available.includes('list') && chip('list', 'List', 'List view', <TableRowsIcon fontSize="small" />)}
        {view.available.includes('kanban') && chip('kanban', 'Kanban', 'Kanban view', <ViewKanbanIcon fontSize="small" />)}
        {view.available.includes('tree') && chip('tree', 'Tree', 'Tree view', <AccountTreeIcon fontSize="small" />)}
      </ToggleButtonGroup>
    )
  }

  const densityToggle = (labeled: boolean) => {
    if (!density) return null
    const chip = (value: Density, label: string, aria: string, icon: React.ReactNode) => {
      const btn = (
        <ToggleButton value={value} aria-label={aria} sx={{ px: labeled ? 1.25 : 1, gap: labeled ? 0.75 : 0, textTransform: 'none' }}>
          {icon}
          {labeled ? label : null}
        </ToggleButton>
      )
      return labeled ? <React.Fragment key={value}>{btn}</React.Fragment> : <Tooltip key={value} title={aria}>{btn}</Tooltip>
    }
    return (
      <ToggleButtonGroup
        size="small"
        exclusive
        value={density.value}
        onChange={(_, v) => { if (v) density.onChange(v as Density); }}
      >
        {chip('comfortable', 'Comfortable', 'Comfortable density', <DensityMediumIcon fontSize="small" />)}
        {chip('compact', 'Compact', 'Compact density', <DensitySmallIcon fontSize="small" />)}
      </ToggleButtonGroup>
    )
  }

  const wrapBarChrome = (id: ListChromeId, node: React.ReactNode) => {
    if (node == null || node === false) return null
    return (
      <Box key={id} sx={{ display: 'flex', alignItems: 'center' }}>
        {node}
      </Box>
    )
  }

  const parts: { id: ListChromeId; barNode: React.ReactNode }[] = [
    { id: 'View', barNode: viewToggle(false) },
    { id: 'Filters', barNode: filterPicker },
    { id: 'GroupBy', barNode: fieldPickers },
    { id: 'Columns', barNode: barActions ?? actions },
    { id: 'Density', barNode: densityToggle(false) },
  ]
  const barChrome = parts
    .filter((p) => slotOf(p.id) === 'bar')
    .map((p) => wrapBarChrome(p.id, p.barNode))
    .filter(Boolean)

  const searchField = search ? (
    <TextField
      size="small"
      placeholder={search.placeholder ?? 'Search...'}
      value={search.value}
      onChange={(e) => search.onChange(e.target.value)}
      inputRef={searchInputRef}
      sx={{ width: 240 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
        ),
        endAdornment: search.value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={() => search.onChange('')}
              edge="end"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  ) : null

  return (
  <CommandBar
    toolbar={{ embedded, mark: 'list' }}
    secondary={secondary}
    overflow={overflow}
    onPin={onPin}
    onResetPins={onResetPins}
    leading={
      <>
        {title && <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{title}</Typography>}
        {busy && <CircularProgress size={16} />}
      </>
    }
    primary={primaryAction ? [{
      id: 'Create',
      label: primaryAction.label,
      icon: primaryAction.icon,
      onClick: primaryAction.onClick,
    }] : []}
    utilities={
      (searchField || filters || barChrome.length > 0) ? (
        <>
          {searchField}
          {filters}
          {barChrome}
        </>
      ) : undefined
    }
    trailing={
      onOpenSettings ? (
        <IconButton size="small" aria-label="List settings" onClick={onOpenSettings}>
          <SettingsIcon fontSize="small" />
        </IconButton>
      ) : undefined
    }
    moreExtras={undefined}
  />
  )
}
